import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  MessageSquare, 
  ArrowLeft, 
  Calendar,
  CheckCircle,
  XCircle,
  ExternalLink,
  Loader2,
  Eye,
  EyeOff,
  Trash2,
  Save,
  Database,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntegrationsPage = () => {
  const {  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [integrations, setIntegrations] = useState(null);
  const [calendlyLink, setCalendlyLink] = useState(null);
  
  // Form state
  const [calendlyToken, setCalendlyToken] = useState('');
  const [showCalendlyToken, setShowCalendlyToken] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientSecret, setGoogleClientSecret] = useState('');
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);
  const [outlookTenantId, setOutlookTenantId] = useState('');
  const [outlookClientId, setOutlookClientId] = useState('');
  const [outlookClientSecret, setOutlookClientSecret] = useState('');
  const [showOutlookSecret, setShowOutlookSecret] = useState(false);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const [statusRes, linkRes] = await Promise.all([
        axios.get(`${API}/integrations/status`),
        axios.get(`${API}/calendly/scheduling-link`)
      ]);
      setIntegrations(statusRes.data);
      setCalendlyLink(linkRes.data);
    } catch (error) {
      console.error('Error fetching integrations:', error);
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const saveCalendly = async () => {
    if (!calendlyToken.trim()) {
      toast.error('Please enter your Calendly API token');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/auth/integrations`, 
        { calendly_token: calendlyToken },
        
      );
      toast.success('Calendly connected successfully!');
      setCalendlyToken('');
      fetchIntegrations();
    } catch (error) {
      console.error('Error saving Calendly:', error);
      toast.error('Failed to save Calendly token');
    } finally {
      setSaving(false);
    }
  };

  const saveGoogle = async () => {
    if (!googleClientId.trim() || !googleClientSecret.trim()) {
      toast.error('Please enter both Client ID and Client Secret');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/auth/integrations`, 
        { google_client_id: googleClientId, google_client_secret: googleClientSecret },
        
      );
      toast.success('Google Calendar credentials saved!');
      setGoogleClientId('');
      setGoogleClientSecret('');
      fetchIntegrations();
    } catch (error) {
      console.error('Error saving Google:', error);
      toast.error('Failed to save Google credentials');
    } finally {
      setSaving(false);
    }
  };

  const saveOutlook = async () => {
    if (!outlookTenantId.trim() || !outlookClientId.trim() || !outlookClientSecret.trim()) {
      toast.error('Please enter Tenant ID, Client ID and Client Secret');
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/auth/integrations`, 
        { 
          outlook_tenant_id: outlookTenantId, 
          outlook_client_id: outlookClientId, 
          outlook_client_secret: outlookClientSecret 
        },
        
      );
      toast.success('Outlook credentials saved!');
      setOutlookTenantId('');
      setOutlookClientId('');
      setOutlookClientSecret('');
      fetchIntegrations();
    } catch (error) {
      console.error('Error saving Outlook:', error);
      toast.error('Failed to save Outlook credentials');
    } finally {
      setSaving(false);
    }
  };

  const removeIntegration = async (provider) => {
    try {
      await axios.delete(`${API}/auth/integrations/${provider}`);
      toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} disconnected`);
      fetchIntegrations();
    } catch (error) {
      console.error('Error removing integration:', error);
      toast.error('Failed to remove integration');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm" className="text-slate-600" data-testid="back-dashboard-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <h1 
            className="text-3xl font-bold text-slate-900 mb-2"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Integrations
          </h1>
          <p className="text-slate-600">
            Connect IntakePal with your existing tools and systems.
          </p>
        </div>

        {/* Case Management Systems - Featured */}
        <Card className="border-teal-200 bg-gradient-to-r from-teal-50 to-emerald-50 mb-8">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-1">Case Management Systems</h2>
                  <p className="text-slate-600 text-sm">
                    Automatically sync new leads to Filevine, Clio, CASEpeer, and 9 more systems.
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="px-2 py-0.5 bg-white/70 rounded text-xs text-slate-600">Filevine</span>
                    <span className="px-2 py-0.5 bg-white/70 rounded text-xs text-slate-600">Clio</span>
                    <span className="px-2 py-0.5 bg-white/70 rounded text-xs text-slate-600">CASEpeer</span>
                    <span className="px-2 py-0.5 bg-white/70 rounded text-xs text-slate-600">+9 more</span>
                  </div>
                </div>
              </div>
              <Link to="/integrations/case-management">
                <Button className="bg-teal-600 hover:bg-teal-700 w-full sm:w-auto" data-testid="cms-integrations-btn">
                  Configure
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Calendar Section Header */}
        <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-slate-600" />
          Calendar Integrations
        </h2>
        <p className="text-slate-600 text-sm mb-6">
          Connect your calendar to allow clients to book consultations directly.
        </p>

        <div className="space-y-6">
          {/* Calendly */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Calendar className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Calendly</CardTitle>
                    <CardDescription>Sync with your Calendly scheduling</CardDescription>
                  </div>
                </div>
                {integrations?.calendly?.valid ? (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm font-medium">
                    <XCircle className="w-4 h-4" />
                    Not Connected
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {integrations?.calendly?.valid && calendlyLink?.configured ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-800 mb-2">Your scheduling link:</p>
                    <a 
                      href={calendlyLink.scheduling_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-green-700 hover:text-green-800 font-medium flex items-center gap-1"
                    >
                      {calendlyLink.scheduling_link}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  
                  {calendlyLink.event_types?.length > 0 && (
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">Available Event Types:</p>
                      <div className="space-y-2">
                        {calendlyLink.event_types.map((et, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg">
                            <div>
                              <p className="font-medium text-slate-900">{et.name}</p>
                              <p className="text-sm text-slate-500">{et.duration} minutes</p>
                            </div>
                            <a 
                              href={et.scheduling_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-teal-600 hover:text-teal-700"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeIntegration('calendly')}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Disconnect Calendly
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-600 text-sm mb-3">
                      To connect Calendly, enter your Personal Access Token. 
                      <a 
                        href="https://calendly.com/integrations/api_webhooks" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-700 ml-1"
                      >
                        Get your token here <ExternalLink className="w-3 h-3 inline" />
                      </a>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="calendly-token">Calendly API Token</Label>
                    <div className="relative">
                      <Input
                        id="calendly-token"
                        type={showCalendlyToken ? 'text' : 'password'}
                        placeholder="Enter your Calendly API token"
                        value={calendlyToken}
                        onChange={(e) => setCalendlyToken(e.target.value)}
                        className="pr-10"
                        data-testid="calendly-token-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCalendlyToken(!showCalendlyToken)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showCalendlyToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    onClick={saveCalendly} 
                    disabled={saving || !calendlyToken.trim()}
                    className="bg-teal-500 hover:bg-teal-600"
                    data-testid="save-calendly-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Connect Calendly
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Google Calendar */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <path d="M19.5 3.5H4.5C3.4 3.5 2.5 4.4 2.5 5.5V19.5C2.5 20.6 3.4 21.5 4.5 21.5H19.5C20.6 21.5 21.5 20.6 21.5 19.5V5.5C21.5 4.4 20.6 3.5 19.5 3.5Z" stroke="#DC2626" strokeWidth="2"/>
                      <path d="M16 2V5" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M8 2V5" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"/>
                      <path d="M2.5 9H21.5" stroke="#DC2626" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Google Calendar</CardTitle>
                    <CardDescription>Sync with your Google Calendar</CardDescription>
                  </div>
                </div>
                {integrations?.google_calendar?.configured ? (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                    Setup Required
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {integrations?.google_calendar?.configured ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-green-800 font-medium">Google Calendar credentials configured</p>
                    <p className="text-sm text-green-700 mt-1">OAuth flow will be triggered when clients book.</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeIntegration('google')}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Google Calendar
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-600 text-sm mb-2">
                      To enable Google Calendar, you need OAuth credentials from Google Cloud Console.
                    </p>
                    <a 
                      href="https://console.cloud.google.com/apis/credentials" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
                    >
                      Create credentials in Google Cloud Console
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="google-client-id">Client ID</Label>
                      <Input
                        id="google-client-id"
                        placeholder="Enter Google Client ID"
                        value={googleClientId}
                        onChange={(e) => setGoogleClientId(e.target.value)}
                        data-testid="google-client-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="google-client-secret">Client Secret</Label>
                      <div className="relative">
                        <Input
                          id="google-client-secret"
                          type={showGoogleSecret ? 'text' : 'password'}
                          placeholder="Enter Google Client Secret"
                          value={googleClientSecret}
                          onChange={(e) => setGoogleClientSecret(e.target.value)}
                          className="pr-10"
                          data-testid="google-client-secret-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowGoogleSecret(!showGoogleSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showGoogleSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={saveGoogle} 
                    disabled={saving || !googleClientId.trim() || !googleClientSecret.trim()}
                    className="bg-teal-500 hover:bg-teal-600"
                    data-testid="save-google-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Google Credentials
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Microsoft Outlook */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <path d="M4 4H20V20H4V4Z" stroke="#0078D4" strokeWidth="2"/>
                      <path d="M4 8H20" stroke="#0078D4" strokeWidth="2"/>
                      <path d="M8 4V20" stroke="#0078D4" strokeWidth="2"/>
                    </svg>
                  </div>
                  <div>
                    <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Microsoft Outlook</CardTitle>
                    <CardDescription>Sync with your Outlook calendar</CardDescription>
                  </div>
                </div>
                {integrations?.outlook?.configured ? (
                  <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    Configured
                  </span>
                ) : (
                  <span className="flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                    Setup Required
                  </span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {integrations?.outlook?.configured ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-green-800 font-medium">Microsoft Outlook credentials configured</p>
                    <p className="text-sm text-green-700 mt-1">OAuth flow will be triggered when clients book.</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => removeIntegration('outlook')}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remove Outlook
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-slate-600 text-sm mb-2">
                      To enable Microsoft Outlook, you need app credentials from Azure Portal.
                    </p>
                    <a 
                      href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-teal-600 hover:text-teal-700 text-sm font-medium flex items-center gap-1"
                    >
                      Register app in Azure Portal
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                  <div className="grid gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="outlook-tenant-id">Tenant ID</Label>
                      <Input
                        id="outlook-tenant-id"
                        placeholder="Enter Azure Tenant ID"
                        value={outlookTenantId}
                        onChange={(e) => setOutlookTenantId(e.target.value)}
                        data-testid="outlook-tenant-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="outlook-client-id">Client ID</Label>
                      <Input
                        id="outlook-client-id"
                        placeholder="Enter Application (Client) ID"
                        value={outlookClientId}
                        onChange={(e) => setOutlookClientId(e.target.value)}
                        data-testid="outlook-client-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="outlook-client-secret">Client Secret</Label>
                      <div className="relative">
                        <Input
                          id="outlook-client-secret"
                          type={showOutlookSecret ? 'text' : 'password'}
                          placeholder="Enter Client Secret"
                          value={outlookClientSecret}
                          onChange={(e) => setOutlookClientSecret(e.target.value)}
                          className="pr-10"
                          data-testid="outlook-client-secret-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowOutlookSecret(!showOutlookSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showOutlookSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={saveOutlook} 
                    disabled={saving || !outlookTenantId.trim() || !outlookClientId.trim() || !outlookClientSecret.trim()}
                    className="bg-teal-500 hover:bg-teal-600"
                    data-testid="save-outlook-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Outlook Credentials
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="mt-8 p-6 bg-teal-50 rounded-xl border border-teal-200">
          <h3 className="font-semibold text-teal-900 mb-2">How it works</h3>
          <p className="text-teal-700 text-sm">
            Once connected, your calendar integration will appear as an option for clients after they complete the intake form. 
            They can click to book directly on your calendar, and the appointment will sync automatically.
          </p>
        </div>
      </main>
    </div>
  );
};

export default IntegrationsPage;
